import React, { useState, useEffect, Fragment } from 'react';
// import { Deck, Slide } from 'spectacle';
import { ThemeProvider } from 'styled-components';
import { Helmet } from 'react-helmet';
import theme from './theme';
import { mergeTheme, oneTheme } from './deckTheme';
import innerPresentationBack from './imgs/02-inner_presentation_one.png';
import vocationHeaderLogo from './imgs/vocation_one_horizontal.svg';

import { ResetCSS, GlobalStyle, PageWrapper, PageNumber } from './styles';
// pages
// import VocationOneTopPage from './Pages/Top';
// import VocationOneClosePage from './Pages/Close';
import PresentationPage from './Pages/Presentation';
import InfoDataPage from './Pages/InfoData';
import OrientationDescriptionPage from './Pages/OrientationDescription';
import AreasGraphPage from './Pages/AreasGraph';
import AreasDescriptionPage from './Pages/AreasDescription';
import PreferencesGraphPage from './Pages/PreferencesGraph';
import PreferencesClarityPage from './Pages/PreferencesClarity';
import KnowledgeAreasGraphPage from './Pages/KnowledgeAreasGraph';
import KnowledgeAreasGraphContPage from './Pages/KnowledgeAreasGraphCont';
import CarrerasTopPage from './Pages/CarrerasTop';
import CarrerasTopDescPage from './Pages/CarrerasTopDesc';
import DysfunctionalBeliefsFirstPage from './Pages/DysfunctionalBeliefsFirst';
import DysfunctionalBeliefsSecondPage from './Pages/DysfunctionalBeliefsSecond';
import DysfunctionalBeliefsThirdPage from './Pages/DysfunctionalBeliefsThird';
import DysfunctionalBeliefsForthPage from './Pages/DysfunctionalBeliefsForth';
import DysfunctionalBeliefsFirstContPage from './Pages/DysfunctionalBeliefsFirstContPage';
import DecisionMakingFirstPage from './Pages/DecisionMakingFirst';
import DecisionMakingSecondPage from './Pages/DecisionMakingSecond';
import DecisionMakingThirdPage from './Pages/DecisionMakingThird';
import VocationalMaturityFirstPage from './Pages/VocationalMaturityFirst';
import VocationalMaturitySecondPage from './Pages/VocationalMaturitySecond';
import VocationalMaturityThirdPage from './Pages/VocationalMaturityThird';
import VocationalMaturityForthPage from './Pages/VocationalMaturityForth';
import ClosingPage from './Pages/ClosingPage';
import CoverPage from './Pages/CoverPage';
import BackCover from './Pages/BackCover';

const Deck = props => {
  // console.log({ props });
  const style = {
    // position: 'relative',
    // height: `${props.theme.size.height}px`,
    // width: `${props.theme.size.width}px`,
    backgroundColor: props.backgroundColor || '#FFFFFF',
    page: 'single', // css page definition,
  };
  return <div style={style}>{props.children}</div>;
};

const Slide = props => {
  const style = {
    position: 'relative',
    display: 'block',
    height: `1123px`,
    width: `794px`,
    marginTop: '-2px',
    backgroundColor: props.backgroundColor || 'transparent',
    overflow: 'hidden'
  };
  return (
    <div className='page' id='single' style={style}>
      {props.children}
    </div>
  );
};

// Slider Deck theme
const deckTheme = mergeTheme(oneTheme);

// https://github.com/FormidableLabs/spectacle/blob/master/docs/content/advanced-concepts.md#query-parameters
// add to url
// &exportMode=true
export const SlidePage = ({ number, children, background, header = true }) => {
  const isEven = number % 2 === 0;
  // console.log({ number, isEven });
  return (
    <PageWrapper backgroundImage={background}>
      {header && (
        <div
          style={{
            position: 'absolute',
            top: 85,
            left: 0,
            right: 0,
            marginLeft: 92,
            marginRight: 75
          }}
        >
          <img
            src={vocationHeaderLogo}
            alt='vocation one header'
            style={{ height: 34 }}
          />
          <div
            style={{ height: 4, background: '#5a86e8', width: '100%' }}
          ></div>
        </div>
      )}
      {children}
      {number && <PageNumber even={isEven}>{number}</PageNumber>}
    </PageWrapper>
  );
};

const getConsistency = userData => {
  if (!userData) return {};
  const DIMENSIONS = ['CN', 'FM', 'CD', 'AP', 'LM', 'IP', 'SE', 'AL'];
  const SUM_FIELDS = DIMENSIONS.map(dim => `${dim}_SUM`);
  // console.log({ DIMENSIONS, SUM_FIELDS });
  const TOT_NUM_ITEMS = 16;
  const SUMS = SUM_FIELDS.reduce((acc, field) => {
    const value = userData[field] ? parseFloat(userData[field]) : 0;
    const squared = value * value;
    const consistency = squared / TOT_NUM_ITEMS ** 2;
    const consistencyValue = consistency * 100;
    const ratio = value / TOT_NUM_ITEMS;
    const ratioDelta = ratio - consistency;
    const consistencyTransformed = ratio + ratioDelta;
    const sign = Math.sign(value);
    return [
      ...acc,
      {
        key: field.replace('_SUM', ''),
        sum: value,
        squared: squared,
        consistencyValue: consistencyValue * sign,
        consistency: consistencyTransformed * 100 * sign
      }
    ];
  }, []);
  // console.log({ SUMS });
  const squaredSum = SUMS.reduce((acc, sum) => acc + sum.squared, 0);
  const divisionValue = TOT_NUM_ITEMS ** 2 * 3;
  // console.log({ squaredSum, divisionValue });
  const globalConsistency = squaredSum / divisionValue;

  return { dimensions: SUMS, global: globalConsistency * 100 };
};
// /r/59720d95-cbd5-4550-86ca-995e75c3caae?exportMode=true

// http://localhost:3000/r/e343a2d1-023e-4159-b0f6-18f55ca1935d?slideIndex=0&stepIndex=0

const VocationOne = ({ campaign, response, data }) => {
  const [loading, setLoading] = useState(true);
  // const [error, setError] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // console.log({ params });
    const parseUser = async () => {
      // add working data for compatibility and to have all data in one place
      const userData = data.workingData
        ? { ...data.workingData, ...data }
        : data;
      // console.log({ userData });
      const fields = Object.keys(userData);
      const FIRST_AREA_ITEMS = fields.filter(
        field => field.includes('_FIRST_SUM') && !field.includes('perc_')
      );
      const SECOND_AREA_ITEMS = fields.filter(
        field => field.includes('_SECOND_SUM') && !field.includes('perc_')
      );
      const THIRD_AREA_ITEMS = fields.filter(
        field => field.includes('_THIRD_SUM') && !field.includes('perc_')
      );
      const FOURTH_AREA_ITEMS = fields.filter(
        field => field.includes('_FOURTH_SUM') && !field.includes('perc_')
      );
      const sortedFirstAreaSums = FIRST_AREA_ITEMS.map(fieldKey => ({
        area: fieldKey.replace('_FIRST_SUM', ''),
        value: userData[fieldKey] ? parseFloat(userData[fieldKey]) : 0
      })).sort((a, b) => b.value - a.value);
      const sortedSecondAreaSums = SECOND_AREA_ITEMS.map(fieldKey => ({
        area: fieldKey.replace('_SECOND_SUM', ''),
        value: userData[fieldKey] ? parseFloat(userData[fieldKey]) : 0
      })).sort((a, b) => b.value - a.value);
      const sortedThirdAreaSums = THIRD_AREA_ITEMS.map(fieldKey => ({
        area: fieldKey.replace('_THIRD_SUM', ''),
        value: userData[fieldKey] ? parseFloat(userData[fieldKey]) : 0
      })).sort((a, b) => b.value - a.value);
      const sortedFourthAreaSums = FOURTH_AREA_ITEMS.map(fieldKey => ({
        area: fieldKey.replace('_FOURTH_SUM', ''),
        value: userData[fieldKey] ? parseFloat(userData[fieldKey]) : 0
      })).sort((a, b) => b.value - a.value);
      const decisionFields = [
        'Afrontamiento Productivo',
        'Búsqueda de Apoyo',
        'Afrontamiento No-Producivo'
      ];
      const sortedDecisionStyle = decisionFields
        .map(fieldKey => ({
          field: fieldKey,
          value: userData[fieldKey] ? parseFloat(userData[fieldKey]) : 0
        }))
        .sort((a, b) => b.value - a.value);
      // console.log({ sortedFirstAreaSums, sortedSecondAreaSums });
      // console.log({ sortedDecisionStyle });
      const baseVocationInfo = {
        RUT: userData.rut,
        name: '',
        lastname: '',
        grade: '',
        school: campaign.organization || campaign.title
      };
      const school = userData.school || baseVocationInfo.school;
      // console.log({ FIRST_AREA_ITEMS, sortedFirstAreaSums });
      // console.log({ SECOND_AREA_ITEMS, sortedSecondAreaSums });
      // console.log({ THIRD_AREA_ITEMS, sortedThirdAreaSums });
      // console.log({ userData });
      const consistency = getConsistency(userData);
      // console.log({ consistency });
      const parsedUser = {
        ...baseVocationInfo,
        ...userData,
        consistency,
        school,
        FIRST_AREA_ITEMS,
        SECOND_AREA_ITEMS,
        THIRD_AREA_ITEMS,
        FOURTH_AREA_ITEMS,
        TOP_FIRST_1: sortedFirstAreaSums[0]?.area,
        TOP_FIRST_2: sortedFirstAreaSums[1]?.area,
        TOP_SECOND_1: sortedSecondAreaSums[0]?.area,
        TOP_SECOND_2: sortedSecondAreaSums[1]?.area,
        TOP_THIRD_1: sortedThirdAreaSums[0]?.area,
        TOP_THIRD_2: sortedThirdAreaSums[1]?.area,
        TOP_FOURTH_1: sortedFourthAreaSums[0]?.area,
        TOP_FOURTH_2: sortedFourthAreaSums[1]?.area,
        TOP_DECISION_STYLE: sortedDecisionStyle[0]?.field,
        createdAt: response.createdAt,
        updatedAt: response.updatedAt,
        responseId: response.id,
        responseAccessKey: response.accessKey
      };
      setUser(parsedUser);
      setLoading(false);
    };
    parseUser();
  }, [data]);

  // console.log({ user });
  if (loading) {
    return <div>loading...</div>;
  }
  console.log({ user });
  console.log({ response });

  const {
    TOP_FIRST_AREA_1,
    TOP_FIRST_AREA_2,
    TOP_FIRST_AREA_3,
    TOP_FIRST_AREA_4,
    TOP_SECOND_AREA_1,
    TOP_SECOND_AREA_2,
    TOP_SECOND_AREA_3,
    TOP_SECOND_AREA_4,
    TOP_THIRD_AREA_1,
    TOP_THIRD_AREA_2,
    TOP_THIRD_AREA_3,
    TOP_THIRD_AREA_4,
    TOP_FOURTH_AREA_1,
    TOP_FOURTH_AREA_2,
    TOP_FOURTH_AREA_3,
    TOP_FOURTH_AREA_4
  } = user;
  const allTopCareers = [
    TOP_FIRST_AREA_1,
    TOP_FIRST_AREA_2,
    TOP_FIRST_AREA_3,
    TOP_FIRST_AREA_4,
    TOP_SECOND_AREA_1,
    TOP_SECOND_AREA_2,
    TOP_SECOND_AREA_3,
    TOP_SECOND_AREA_4,
    TOP_THIRD_AREA_1,
    TOP_THIRD_AREA_2,
    TOP_THIRD_AREA_3,
    TOP_THIRD_AREA_4,
    TOP_FOURTH_AREA_1,
    TOP_FOURTH_AREA_2,
    TOP_FOURTH_AREA_3,
    TOP_FOURTH_AREA_4
  ].filter(Boolean);
  const topCareers = [...new Set(allTopCareers)];
  // console.log({ allTopCareers });

  const hasExtraAreaGraphPage = user.TOP_DIM_3;
  const halfLength = hasExtraAreaGraphPage ? 10 : 8;
  const lastLength = topCareers.length + 1 + halfLength;

  // console.log({ topCareers });

  const userSchool = user.school ? `_${user.school.split(' ').join('_')}` : '';
  const userGrade = user.grade ? `${user.grade.split(' ').join('_')}_` : '';
  return (
    <ThemeProvider theme={theme}>
      <Fragment>
        <Helmet>
          <title>{`${userGrade}${user.RUT}${userSchool}_VocationOne`}</title>
        </Helmet>
        <ResetCSS />
        <GlobalStyle />
        <Deck theme={deckTheme} backgroundColor='#fff'>
          {/* <Slide backgroundColor='#fff'>
            <VocationOneTopPage
              user={user}
              background={topBackground}
              header={false}
            />
          </Slide> */}

          <Slide backgroundColor='#5a86e8'>
            <CoverPage user={user} header={false} campaign={campaign} />
          </Slide>

          {/** BLANK PAGE */}
          <Slide backgroundColor='#fff'>
            <SlidePage header={false}></SlidePage>
          </Slide>

          <Slide backgroundColor='#fff'>
            <PresentationPage
              user={user}
              number={1}
              background={innerPresentationBack}
            />
          </Slide>

          <Slide backgroundColor='#fff'>
            <InfoDataPage user={user} number={2} />
          </Slide>

          <Slide backgroundColor='#fff'>
            <OrientationDescriptionPage number={3} />
          </Slide>

          <Slide backgroundColor='#fff'>
            <AreasGraphPage user={user} number={4} />
          </Slide>

          <Slide backgroundColor='#fff'>
            <AreasDescriptionPage user={user} number={5} />
          </Slide>

          {user.TOP_DIM_3 ? (
            <Slide backgroundColor='#fff'>
              <AreasDescriptionPage user={user} number={6} isContinuation />
            </Slide>
          ) : (
            <div></div>
          )}

          <Slide backgroundColor='#fff'>
            <PreferencesGraphPage user={user} number={user.TOP_DIM_3 ? 7 : 6} />
          </Slide>

          <Slide backgroundColor='#fff'>
            <PreferencesClarityPage
              user={user}
              number={user.TOP_DIM_3 ? 8 : 7}
            />
          </Slide>

          <Slide backgroundColor='#fff'>
            <KnowledgeAreasGraphPage
              user={user}
              number={user.TOP_DIM_3 ? 9 : 8}
              isLast={!user.TOP_DIM_3}
            />
          </Slide>

          {user.TOP_DIM_3 ? (
            <Slide backgroundColor='#fff'>
              <KnowledgeAreasGraphContPage user={user} number={halfLength} />
            </Slide>
          ) : (
            <div></div>
          )}

          <Slide backgroundColor='#fff'>
            <CarrerasTopDescPage
              user={user}
              // top={topCareers[0]}
              careers={topCareers}
              number={halfLength + 1}
            />
          </Slide>

          {topCareers.map((top, index) => (
            <Slide backgroundColor='#fff' key={top}>
              <CarrerasTopPage
                user={user}
                number={halfLength + index + 2}
                top={top}
                index={index}
              />
            </Slide>
          ))}

          <Slide backgroundColor='#fff'>
            <DysfunctionalBeliefsFirstPage
              user={user}
              number={lastLength + 1}
            />
          </Slide>

          <Slide backgroundColor='#fff'>
            <DysfunctionalBeliefsFirstContPage
              user={user}
              number={lastLength + 2}
            />
          </Slide>

          <Slide backgroundColor='#fff'>
            <DysfunctionalBeliefsSecondPage
              user={user}
              number={lastLength + 3}
            />
          </Slide>

          <Slide backgroundColor='#fff'>
            <DysfunctionalBeliefsThirdPage
              user={user}
              number={lastLength + 4}
            />
          </Slide>

          <Slide backgroundColor='#fff'>
            <DysfunctionalBeliefsForthPage
              user={user}
              number={lastLength + 5}
            />
          </Slide>

          <Slide backgroundColor='#fff'>
            <DecisionMakingFirstPage user={user} number={lastLength + 6} />
          </Slide>

          <Slide backgroundColor='#fff'>
            <DecisionMakingSecondPage user={user} number={lastLength + 7} />
          </Slide>
          <Slide backgroundColor='#fff'>
            <DecisionMakingThirdPage user={user} number={lastLength + 8} />
          </Slide>

          <Slide backgroundColor='#fff'>
            <VocationalMaturityFirstPage user={user} number={lastLength + 9} />
          </Slide>

          <Slide backgroundColor='#fff'>
            <VocationalMaturitySecondPage
              user={user}
              number={lastLength + 10}
            />
          </Slide>

          <Slide backgroundColor='#fff'>
            <VocationalMaturityThirdPage user={user} number={lastLength + 11} />
          </Slide>

          <Slide backgroundColor='#fff'>
            <VocationalMaturityForthPage user={user} number={lastLength + 12} />
          </Slide>

          <Slide backgroundColor='#fff'>
            <ClosingPage number={lastLength + 13} user={user} />
          </Slide>

          <Slide backgroundColor='#fff'>
            <BackCover backgroundColor='#fff' header={false} />
          </Slide>
        </Deck>
      </Fragment>
    </ThemeProvider>
  );
};

export default VocationOne;
